import { ArrowRight } from "lucide-react";
import { Button, type ButtonProperties } from "../button";
import { Link } from "../link";
import { Section } from "../section";

export interface Event {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  startDateTime: string;
  endDateTime?: string;
  location?: string;
  coverImage?: { src: string; alt?: string };
  type?: string;
  typeLabel?: string;
  speaker?: string;
  youtubeUrl?: string;
  featured?: boolean;
}

export interface EventHighlightProperties {
  title: string;
  description?: string;
  events: Event[];
  backgroundColor?: string;
  textColor?: string;
  buttons?: Array<
    {
      label: string;
      href?: string;
      onClick?: () => void;
    } & Omit<ButtonProperties, "children" | "href" | "onClick">
  >;
  activitiesLabel?: string;
}

// Helper function to format event date
const formatEventDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("nl-NL", { month: "long" });
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return { day, month, hours, minutes, time: `${hours}:${minutes}` };
};

// Event Card Component (extracted for readability)
function EventCard({ event }: { event: Event }) {
  const { day, month, time } = formatEventDate(event.startDateTime);

  return (
    <Link
      key={event.id}
      href={`/${event.slug}`}
      className="group flex items-center gap-6 transition-all hover:translate-x-2 md:gap-8"
    >
      {/* Date Display */}
      <div className="flex min-w-[80px] flex-col items-center md:min-w-[100px]">
        <span className="font-heading text-sm tracking-wide uppercase opacity-60 md:text-base">
          {day} {month}
        </span>
        <span className="font-heading text-4xl leading-none md:text-5xl">
          {time}
        </span>
      </div>

      {/* Event Details */}
      <div className="flex flex-1 flex-col gap-2 border-b pb-6">
        <span className="font-heading text-xs tracking-widest uppercase opacity-60 md:text-sm">
          {event.type}
        </span>
        <h4 className="font-heading text-xl leading-tight md:text-2xl lg:text-3xl">
          {event.title}
        </h4>
        {event.speaker && (
          <small className="font-body text-xs tracking-widest opacity-60">
            Spreker: {event.speaker}
          </small>
        )}
      </div>

      {/* Arrow */}
      <div className="flex items-center">
        <ArrowRight className="text-boldness h-6 w-6 transition-transform group-hover:translate-x-1 md:h-8 md:w-8" />
      </div>
    </Link>
  );
}

export function EventHighlight({
  title,
  description,
  events,
  backgroundColor = "freedom",
  textColor = "boldness",
  buttons,
  activitiesLabel = "ACTIVITEITEN",
}: EventHighlightProperties) {
  // Don't render if no events found
  if (events.length === 0) return null;

  return (
    <Section backgroundColor={backgroundColor} color={textColor}>
      <div className="container mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Header */}
          <div className="flex flex-col justify-start">
            <h2 className="font-heading mb-6 text-5xl tracking-wide uppercase md:text-6xl lg:text-7xl">
              {title}
            </h2>
            {description && (
              <p className="font-body mb-8 text-lg leading-relaxed opacity-60 md:text-xl lg:mb-10">
                {description}
              </p>
            )}
            {buttons && buttons.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {buttons.map((button, index) => (
                  <Button
                    key={index}
                    href={button.href}
                    onClick={button.onClick}
                    {...button}
                  >
                    {button.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Events List */}
          <div className="flex flex-col">
            {/* Header */}
            <div className="border-strategy-blue mb-8 border-b-2 pb-4">
              <h3 className="font-heading text-sm tracking-widest uppercase md:text-base">
                {activitiesLabel}
              </h3>
            </div>

            {/* Events */}
            <div className="flex flex-col gap-6">
              {events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
